import React, { useMemo, useState } from "react"
import { Alert, Container } from "reactstrap"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import Swal from "sweetalert2"
import { Link } from "react-router-dom"

import storage from "services/storage"
import { authUserKey } from "config/API"
import "assets/scss/custom/components/periodic-task.scss"
import { CashMovementsHistoryTable } from "components/periodicTasks"
import { deleteCashMovemnts, getCashMovemnts } from "services/tickets"
import { getCurrentCashOperationDay } from "services/cash-operation-days"
import { getDatePresetRange } from "utils/date-filters"

const defaultDateFilter = { preset: "range", ...getDatePresetRange("yesterday") }

const normalizeFilterValues = (value) => {
  if (Array.isArray(value)) {
    return value
  }

  if (!value || value === "all") {
    return []
  }

  return [value]
}

const CashMovementsHistory = () => {
  document.title = "Historial de movimientos | Punto de venta"
  const permissions = storage.get(authUserKey).user_permissions || []
  const [movementsPage, setMovementsPage] = useState(1)
  const [movementFilters, setMovementFilters] = useState({
    typeFilter: "withdraw",
    searchTxt: "",
    dateFilter: defaultDateFilter,
  })
  const queryClient = useQueryClient()

  const deleteMutation = useMutation((movementId) => deleteCashMovemnts(movementId))

  const queryParams = useMemo(() => {
    const params = {
      ordering: "-created_at",
      per_page: 30,
      offset: (movementsPage - 1) * 30,
    }

    const selectedTypes = normalizeFilterValues(movementFilters.typeFilter)

    if (selectedTypes.length === 1) {
      params.movement_type = selectedTypes[0]
    } else if (selectedTypes.length > 1) {
      params.movement_type__in = selectedTypes.join(",")
    }

    if (movementFilters.searchTxt?.trim()) {
      params.search = movementFilters.searchTxt.trim()
    }

    if (movementFilters.dateFilter?.startDate) {
      params.date__gte = movementFilters.dateFilter.startDate
    }

    if (movementFilters.dateFilter?.endDate) {
      params.date__lte = movementFilters.dateFilter.endDate
    }

    return params
  }, [movementFilters, movementsPage])

  const confirmDeleteMovement = async (movement) => {
    const result = await Swal.fire({
      title: "Eliminar retiro",
      text: "El efectivo regresará a Dinero en resguardo.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    })

    if (!result.isConfirmed) {
      return
    }

    await deleteMutation.mutateAsync(movement.id)
    await queryClient.invalidateQueries(["cash-movements-history"])
    await Swal.fire("Eliminado", "El retiro fue eliminado correctamente.", "success")
  }

  const handleFiltersChange = (nextFilters) => {
    setMovementFilters(nextFilters)
    setMovementsPage(1)
  }

  const cashMovementsQuery = useQuery(
    ["cash-movements-history", movementsPage, movementFilters],
    () => getCashMovemnts(queryParams),
    {
      keepPreviousData: true,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: 5000,
      staleTime: 0,
    }
  )
  const cashOperationDayQuery = useQuery(["cash-operation-day-current"], () => getCurrentCashOperationDay(), {
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 5000,
    staleTime: 0,
  })

  return !permissions.some((e) => e.codename === "view_cashcut") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <div className="page-content" style={{ paddingTop: "1rem" }}>
      <Container fluid>
        {cashMovementsQuery.isError && (
          <Alert color="danger">
            {[cashMovementsQuery.error]
              .filter(Boolean)
              .flatMap((item) => item?.errors?.map((error) => error?.message) || [])
              .join(" ")}
          </Alert>
        )}

        <CashMovementsHistoryTable
          movements={cashMovementsQuery.data?.results || []}
          isLoading={cashMovementsQuery.isLoading && !cashMovementsQuery.data}
          isError={cashMovementsQuery.isError}
          error={cashMovementsQuery.error}
          currentCashOperationDay={cashOperationDayQuery.data || null}
          title="Historial de movimientos"
          description="Ingresos, retiros y transferencias realizadas en caja."
          headerActions={
            <>
              <Link to="/sale?tab=cash" className="btn btn-primary" style={{ marginRight: "10px" }}>
                <i className="fa fa-arrow-left" /> Volver
              </Link>
            </>
          }
          showFilters={true}
          showMovementTypeColumn={true}
          showAccountColumn={true}
          showActionsColumn={true}
          defaultTypeFilter="withdraw"
          defaultDateFilter={defaultDateFilter}
          currentPage={movementsPage}
          totalCount={cashMovementsQuery.data?.count || 0}
          pageSize={30}
          onChangePage={(p) => setMovementsPage(p)}
          onFiltersChange={handleFiltersChange}
          onDeleteMovement={confirmDeleteMovement}
        />
      </Container>
    </div>
  )
}

export default CashMovementsHistory
